import { apiRowToSdeRow } from "@/utils/extraction-api-bridge";
import { mergeIncomingRows } from "@/utils/extraction-row-merge";
import {
    applyAllSuggestionsToRows,
    bulkUpdateFieldRows,
    hasPendingImportRows,
} from "@/utils/sde-bulk-utils";
import { applySyncResultsToRows } from "@/utils/sde-sync-errors";
import {
    buildMetadataFieldInfoMap,
    buildMetadataSdeColumn,
    collectMetadataColumnKeys,
    computeSdeStats,
    countImportableRows,
    createEmptySdeRow,
    duplicateSdeRow,
    filterSdeRows,
    reindexSdeRows,
} from "@/utils/sde-row-utils";

const createState = () => ({
    config: null,
    rows: [],
    draftId: null,
    sessionId: null,
    dirty: false,
    followScroll: true,
    newRowsSinceScroll: 0,
    uiState: {
        search: "",
        chip: "all",
        selectedRowIndices: [],
        showDocumentPanel: false,
    },
    deletedMetadataKeys: [],
});

const mutations = {
    setConfig(state, config) {
        state.config = config;
    },
    setRows(state, rows) {
        state.rows = rows;
    },
    setDraftId(state, draftId) {
        state.draftId = draftId;
    },
    setSessionId(state, sessionId) {
        state.sessionId = sessionId;
    },
    setDirty(state, dirty) {
        state.dirty = dirty;
    },
    setFollowScroll(state, followScroll) {
        state.followScroll = followScroll;
    },
    clearNewRowsSinceScroll(state) {
        state.newRowsSinceScroll = 0;
    },
    resetNewRowsCounter(state) {
        state.newRowsSinceScroll = 0;
        state.followScroll = true;
    },
    appendRowsFromExtraction(state, apiRows) {
        const incoming = apiRows
            .map((row, index) => apiRowToSdeRow(row, state.rows.length + index))
            .filter(Boolean);
        const filteredIncoming = incoming.map((row) => {
            if (!state.deletedMetadataKeys.length) {
                return row;
            }
            const fields = { ...row.fields };
            state.deletedMetadataKeys.forEach((key) => {
                if (fields[key]) {
                    delete fields[key];
                }
            });
            return { ...row, fields };
        });
        state.rows = mergeIncomingRows(state.rows, filteredIncoming);
        if (state.followScroll) {
            state.newRowsSinceScroll = 0;
        } else {
            state.newRowsSinceScroll += filteredIncoming.length;
        }
        state.dirty = true;
    },
    setSearch(state, search) {
        state.uiState.search = search;
    },
    setChip(state, chip) {
        state.uiState.chip = chip;
    },
    setSelectedRowIndices(state, indices) {
        state.uiState.selectedRowIndices = indices;
    },
    toggleDocumentPanel(state) {
        state.uiState.showDocumentPanel = !state.uiState.showDocumentPanel;
    },
    updateCell(state, { rowIndex, key, value }) {
        const row = state.rows.find((item) => item.rowIndex === rowIndex);
        if (!row || !row.fields[key]) {
            return;
        }
        row.fields[key] = {
            ...row.fields[key],
            value,
            confidence: {
                ...row.fields[key].confidence,
                level: "manual",
            },
        };
        state.dirty = true;
    },
    excludeRows(state, { indices, excluded }) {
        state.rows.forEach((row) => {
            if (indices.includes(row.rowIndex)) {
                row.excluded = excluded;
            }
        });
        state.dirty = true;
    },
    insertEmptyRow(state) {
        const columns = state.config?.columns || [];
        const newRow = createEmptySdeRow(columns);
        state.rows = reindexSdeRows([newRow, ...state.rows]);
        state.dirty = true;
    },
    duplicateRows(state, { indices }) {
        const toClone = state.rows.filter((row) => indices.includes(row.rowIndex));
        const clones = toClone.map((row) => duplicateSdeRow(row));
        state.rows = reindexSdeRows([...clones, ...state.rows]);
        state.dirty = true;
    },
    deleteMetadataKey(state, key) {
        if (!state.deletedMetadataKeys.includes(key)) {
            state.deletedMetadataKeys.push(key);
        }
        state.rows.forEach((row) => {
            if (row.fields[key]) {
                row.fields[key] = {
                    ...row.fields[key],
                    deleted: true,
                };
            }
        });
        state.dirty = true;
    },
    bulkUpdateField(state, { fieldKey, value, scope, filteredRows, selectedIndices }) {
        state.rows = bulkUpdateFieldRows(
            state.rows,
            filteredRows,
            selectedIndices,
            fieldKey,
            value,
            scope
        );
        state.dirty = true;
    },
    applyAllSuggestions(state) {
        state.rows = applyAllSuggestionsToRows(state.rows);
        state.dirty = true;
    },
    markRowsFromSyncPayload(state, arg) {
        const syncPayload = Array.isArray(arg) ? arg : arg?.syncPayload;
        const metadataFieldInfoMap = Array.isArray(arg) ? undefined : arg?.metadataFieldInfoMap;
        const columns = Array.isArray(arg) ? state.config?.columns : arg?.columns;
        const persistSuccess = Array.isArray(arg) ? undefined : arg?.persistSuccess;
        state.rows = applySyncResultsToRows(state.rows, syncPayload, {
            metadataFieldInfoMap,
            columns: columns || state.config?.columns || [],
            persistSuccess,
        });
    },
    reset(state) {
        Object.assign(state, createState());
    },
};

const getters = {
    metadataColumnKeys(state, getters, rootState) {
        return collectMetadataColumnKeys(state.rows, getters.visibleColumns, {
            productTypes: rootState.producttypes?.producttypes,
            deletedMetadataKeys: state.deletedMetadataKeys,
        });
    },
    visibleColumns(state) {
        const columns = state.config?.columns || [];
        if (!state.config?.pricesIncludeVat) {
            return columns.filter((col) => col.key !== "unit_price_incl_vat");
        }
        return columns.filter((col) => col.key !== "unit_price_excl_vat");
    },
    filteredRows(state, getters) {
        return filterSdeRows(state.rows, {
            search: state.uiState.search,
            chip: state.uiState.chip,
            columns: getters.allSearchColumns,
        });
    },
    allSearchColumns(state, getters, rootState) {
        const seen = new Set();
        const merged = [];
        const fieldInfoMap = buildMetadataFieldInfoMap(rootState.producttypes?.producttypes);

        const append = (column) => {
            if (!column?.key || seen.has(column.key)) {
                return;
            }
            seen.add(column.key);
            merged.push(column);
        };

        getters.visibleColumns.forEach(append);
        getters.metadataColumnKeys.forEach((key) => {
            append(buildMetadataSdeColumn(key, fieldInfoMap));
        });
        return merged;
    },
    paginatedRows(state, getters) {
        return getters.filteredRows;
    },
    stats(state, getters) {
        return computeSdeStats(state.rows, getters.filteredRows);
    },
    importableCount(state) {
        return countImportableRows(state.rows);
    },
    hasPendingRows(state) {
        return hasPendingImportRows(state.rows);
    },
    suggestionCount(state) {
        return state.rows.filter((row) =>
            Object.values(row.fields || {}).some(
                (field) =>
                    field?.confidence?.suggestion &&
                    field.value !== field.confidence.suggestion &&
                    !field.deleted
            )
        ).length;
    },
};

export default () => ({
    namespaced: true,
    state: createState(),
    mutations,
    getters,
});
